import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MATCH_TYPES,
  buildBackup,
  exportCsv,
  formatMoney,
  parseAmountToSen,
  uniqueCategoryId,
  type MerchantRule,
} from '@wis/shared';
import { useApp } from '../state/AppContext.js';
import { db } from '../db/database.js';
import {
  applyRulesToUnreviewed,
  archiveCategory,
  deleteAllLocalData,
  deleteRule,
  saveCategory,
  saveRule,
} from '../db/repository.js';
import { retryFailed, runSync } from '../sync/engine.js';
import {
  AccountError,
  isSignedIn,
  readDeploymentStatus,
  registerAccount,
  signIn,
  signOut,
} from '../sync/account.js';
import {
  Button,
  Field,
  Notice,
  Segmented,
  Select,
  Sheet,
  Switch,
  TextInput,
} from '../components/primitives.js';

export function Settings(): JSX.Element {
  const { settings, categories, accounts, rules, transactions, sync, updateSettings, toast } =
    useApp();

  const [sheet, setSheet] = useState<
    'budget' | 'accounts' | 'categories' | 'rules' | 'sync' | 'delete' | null
  >(null);

  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <div className="settings-group">
        <button type="button" className="settings-row" onClick={() => setSheet('budget')}>
          <span className="settings-row__label">Monthly budget</span>
          <span className="settings-row__value">
            {settings.monthlyBudgetSen > 0
              ? formatMoney(settings.monthlyBudgetSen, { currency: settings.currency })
              : 'Not set'}
          </span>
        </button>
        <div className="settings-row">
          <span className="settings-row__label">Currency</span>
          <span className="settings-row__value">{settings.currency} · RM 0.00</span>
        </div>
        <div className="settings-row">
          <span className="settings-row__label">Time zone</span>
          <span className="settings-row__value">{settings.timeZone}</span>
        </div>
        <div className="settings-row">
          <span className="settings-row__label">Appearance</span>
          <div style={{ width: '11rem' }}>
            <Segmented
              label="Appearance"
              value={settings.theme}
              options={[
                { value: 'system', label: 'Auto' },
                { value: 'light', label: 'Light' },
                { value: 'dark', label: 'Dark' },
              ]}
              onChange={(theme) => void updateSettings({ theme })}
            />
          </div>
        </div>
      </div>

      <div className="settings-group">
        <button type="button" className="settings-row" onClick={() => setSheet('accounts')}>
          <span className="settings-row__label">Accounts and card labels</span>
          <span className="settings-row__value">{accounts.length}</span>
        </button>
        <button type="button" className="settings-row" onClick={() => setSheet('categories')}>
          <span className="settings-row__label">Categories</span>
          <span className="settings-row__value">
            {categories.filter((category) => !category.archived).length}
          </span>
        </button>
        <button type="button" className="settings-row" onClick={() => setSheet('rules')}>
          <span className="settings-row__label">Merchant rules</span>
          <span className="settings-row__value">{rules.length}</span>
        </button>
      </div>

      <div className="settings-group">
        <Link className="settings-row" to="/import">
          <span className="settings-row__label">Import</span>
          <span className="settings-row__value">Receipts, CSV, backups</span>
        </Link>
        <ExportRow />
        <button type="button" className="settings-row" onClick={() => setSheet('sync')}>
          <span className="settings-row__label">Cloud sync</span>
          <span className="settings-row__value">
            {settings.syncEnabled ? syncSummary(sync) : 'Off'}
          </span>
        </button>
      </div>

      <div className="settings-group">
        <Link className="settings-row" to="/shortcuts">
          <span className="settings-row__label">iPhone Shortcuts</span>
          <span className="settings-row__value">Setup guide</span>
        </Link>
        <Link className="settings-row" to="/privacy">
          <span className="settings-row__label">Privacy</span>
          <span className="settings-row__value">Where your data lives</span>
        </Link>
      </div>

      <div className="settings-group">
        <button
          type="button"
          className="settings-row"
          onClick={() => setSheet('delete')}
          style={{ color: 'var(--danger)' }}
        >
          <span className="settings-row__label">Delete all data</span>
          <span className="settings-row__value">{transactions.length} records</span>
        </button>
      </div>

      <p className="tiny faint" style={{ textAlign: 'center', marginTop: 'var(--space-6)' }}>
        What I Spent · everything is stored on this device unless you switch on cloud sync.
      </p>

      <BudgetSheet open={sheet === 'budget'} onClose={() => setSheet(null)} />
      <AccountsSheet open={sheet === 'accounts'} onClose={() => setSheet(null)} />
      <CategoriesSheet open={sheet === 'categories'} onClose={() => setSheet(null)} />
      <RulesSheet open={sheet === 'rules'} onClose={() => setSheet(null)} />
      <SyncSheet open={sheet === 'sync'} onClose={() => setSheet(null)} />
      <DeleteSheet open={sheet === 'delete'} onClose={() => setSheet(null)} onDone={() => toast('All data deleted')} />
    </div>
  );
}

function syncSummary(sync: ReturnType<typeof useApp>['sync']): string {
  if (sync.failedCount > 0) return `${sync.failedCount} failed`;
  if (sync.pendingCount > 0) return `${sync.pendingCount} queued`;
  if (sync.lastSyncedAt) return `Synced ${sync.lastSyncedAt.slice(11, 16)}`;
  return 'On';
}

/* ---------- export ---------- */

function ExportRow(): JSX.Element {
  const { transactions, categories, accounts, rules, settings, toast } = useApp();
  const [busy, setBusy] = useState(false);

  function download(filename: string, content: string, type: string): void {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    // Revoke on the next tick so Safari has taken the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function handleExport(format: 'csv' | 'json'): Promise<void> {
    setBusy(true);
    try {
      // Deleted records are included in a backup so a restore cannot resurrect them.
      const all = await db.transactions.toArray();
      const stamp = new Date().toISOString().slice(0, 10);

      if (format === 'csv') {
        download(
          `what-i-spent-${stamp}.csv`,
          exportCsv(
            all.filter((transaction) => !transaction.deletedAt),
            categories,
          ),
          'text/csv;charset=utf-8',
        );
      } else {
        const backup = buildBackup({
          transactions: all,
          categories,
          accounts,
          merchantRules: rules,
          settings,
        });
        download(
          `what-i-spent-backup-${stamp}.json`,
          JSON.stringify(backup, null, 2),
          'application/json',
        );
      }
      toast(`Exported ${transactions.length} records`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings-row">
      <span className="settings-row__label">Export</span>
      <div className="row">
        <Button onClick={() => handleExport('csv')} disabled={busy}>
          CSV
        </Button>
        <Button onClick={() => handleExport('json')} disabled={busy}>
          Backup
        </Button>
      </div>
    </div>
  );
}

/* ---------- budget ---------- */

function BudgetSheet({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const { settings, updateSettings } = useApp();
  const [text, setText] = useState(
    settings.monthlyBudgetSen > 0 ? (settings.monthlyBudgetSen / 100).toFixed(2) : '',
  );
  const [error, setError] = useState<string | undefined>();

  async function handleSave(): Promise<void> {
    if (!text.trim()) {
      await updateSettings({ monthlyBudgetSen: 0 });
      onClose();
      return;
    }
    try {
      await updateSettings({ monthlyBudgetSen: Math.abs(parseAmountToSen(text)) });
      onClose();
    } catch {
      setError('Enter an amount like 1500.00, or leave it blank for no budget.');
    }
  }

  return (
    <Sheet open={open} title="Monthly budget" onClose={onClose}>
      <Field
        label="Budget"
        error={error}
        hint="Leave blank to track spending without a target. The budget resets on the first of each month, in your local time zone."
      >
        {(props) => (
          <TextInput
            {...props}
            inputMode="decimal"
            value={text}
            placeholder="1500.00"
            onChange={(event) => setText(event.target.value.replace(/[^0-9.,]/g, ''))}
          />
        )}
      </Field>
      <Button variant="accent" block onClick={handleSave}>
        Save budget
      </Button>
    </Sheet>
  );
}

/* ---------- accounts ---------- */

function AccountsSheet({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const { accounts, settings, updateSettings } = useApp();
  const [label, setLabel] = useState('');

  async function handleAdd(): Promise<void> {
    const trimmed = label.trim();
    if (!trimmed) return;
    await db.accounts.put({
      id: crypto.randomUUID(),
      label: trimmed,
      kind: 'other',
      isDefault: false,
      archived: false,
      updatedAt: new Date().toISOString(),
    });
    setLabel('');
  }

  return (
    <Sheet open={open} title="Accounts and card labels" onClose={onClose}>
      <Notice tone="info">
        These are labels only. The app never asks for and cannot store a card number, a PIN or a
        bank login.
      </Notice>

      <div className="settings-group" style={{ marginTop: 'var(--space-4)' }}>
        {accounts.map((account) => (
          <div key={account.id} className="settings-row">
            <span className="settings-row__label">{account.label}</span>
            <div className="row">
              {settings.defaultAccountLabel === account.label ? (
                <span className="badge">Default</span>
              ) : (
                <Button
                  variant="quiet"
                  onClick={() => void updateSettings({ defaultAccountLabel: account.label })}
                >
                  Make default
                </Button>
              )}
              <Button
                variant="quiet"
                aria-label={`Remove ${account.label}`}
                onClick={() => void db.accounts.delete(account.id)}
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Field label="Add a label" hint="For example: Apple Pay, MAE, TNG eWallet, Cash.">
        {(props) => (
          <TextInput
            {...props}
            value={label}
            autoCapitalize="words"
            onChange={(event) => setLabel(event.target.value)}
          />
        )}
      </Field>
      <Button variant="primary" block onClick={handleAdd} disabled={!label.trim()}>
        Add label
      </Button>
    </Sheet>
  );
}

/* ---------- categories ---------- */

function CategoriesSheet({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const { categories, toast } = useApp();
  const [name, setName] = useState('');

  async function handleAdd(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) return;
    const id = uniqueCategoryId(
      trimmed,
      categories.map((category) => category.id),
    );
    await saveCategory({
      id,
      name: trimmed,
      tone: 'slate',
      icon: '•',
      isBuiltIn: false,
      excludeFromSpending: false,
      archived: false,
      sortOrder: categories.length,
      updatedAt: new Date().toISOString(),
    });
    setName('');
  }

  return (
    <Sheet open={open} title="Categories" onClose={onClose}>
      <div className="settings-group">
        {categories.map((category) => (
          <div key={category.id} className="settings-row">
            <span className="settings-row__label">
              {category.icon} {category.name}
              {category.excludeFromSpending ? (
                <span className="badge" style={{ marginLeft: 'var(--space-2)' }}>
                  not spending
                </span>
              ) : null}
              {category.archived ? (
                <span className="badge" style={{ marginLeft: 'var(--space-2)' }}>
                  archived
                </span>
              ) : null}
            </span>
            {!category.archived && category.id !== 'other' ? (
              <Button
                variant="quiet"
                onClick={async () => {
                  const moved = await archiveCategory(category.id, 'other');
                  toast(
                    moved > 0
                      ? `Archived. ${moved} payments moved to Other.`
                      : 'Archived.',
                  );
                }}
              >
                Archive
              </Button>
            ) : null}
          </div>
        ))}
      </div>

      <Notice tone="info">
        Archiving never deletes spending. Anything filed under the category moves to Other so the
        history stays complete.
      </Notice>

      <Field label="New category">
        {(props) => (
          <TextInput
            {...props}
            value={name}
            autoCapitalize="words"
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </Field>
      <Button variant="primary" block onClick={handleAdd} disabled={!name.trim()}>
        Add category
      </Button>
    </Sheet>
  );
}

/* ---------- rules ---------- */

function RulesSheet({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const { rules, categories, toast } = useApp();
  const [pattern, setPattern] = useState('');
  const [matchType, setMatchType] = useState<MerchantRule['matchType']>('contains');
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? 'other');

  async function handleAdd(): Promise<void> {
    const trimmed = pattern.trim();
    if (!trimmed) return;
    await saveRule({
      id: crypto.randomUUID(),
      pattern: trimmed,
      matchType,
      categoryId,
      renameTo: null,
      priority: 100,
      enabled: true,
      updatedAt: new Date().toISOString(),
    });
    setPattern('');
    const changed = await applyRulesToUnreviewed();
    toast(changed > 0 ? `Rule added. ${changed} unreviewed records recategorised.` : 'Rule added.');
  }

  return (
    <Sheet open={open} title="Merchant rules" onClose={onClose}>
      <Notice tone="info">
        Rules run on this device and always win over the built-in guesses. They only change records
        you have not confirmed yet -- a rule added today never rewrites a decision you made last
        month.
      </Notice>

      {rules.length > 0 ? (
        <div className="settings-group" style={{ marginTop: 'var(--space-4)' }}>
          {rules.map((rule) => (
            <div key={rule.id} className="settings-row">
              <span className="settings-row__label">
                {rule.matchType === 'equals' ? 'is' : rule.matchType === 'starts-with' ? 'starts with' : 'contains'}{' '}
                &ldquo;{rule.pattern}&rdquo;
                <span className="settings-row__value" style={{ display: 'block' }}>
                  → {categories.find((category) => category.id === rule.categoryId)?.name ?? rule.categoryId}
                </span>
              </span>
              <Button variant="quiet" onClick={() => void deleteRule(rule.id)}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      <Field label="When the merchant" hint='For example: "grab" contains → Transport.'>
        {(props) => (
          <Select
            {...props}
            value={matchType}
            onChange={(event) => setMatchType(event.target.value as MerchantRule['matchType'])}
          >
            {MATCH_TYPES.map((type) => (
              <option key={type} value={type}>
                {type === 'equals' ? 'is exactly' : type === 'starts-with' ? 'starts with' : 'contains'}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="This text">
        {(props) => (
          <TextInput
            {...props}
            value={pattern}
            autoCapitalize="none"
            onChange={(event) => setPattern(event.target.value)}
          />
        )}
      </Field>

      <Field label="File it under">
        {(props) => (
          <Select {...props} value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Button variant="primary" block onClick={handleAdd} disabled={!pattern.trim()}>
        Add rule
      </Button>
    </Sheet>
  );
}

/* ---------- sync ---------- */

/**
 * Where the deployment stands: does it still need its one account, and is this
 * device signed in to it?
 *
 * Worth knowing because a Shortcut capture is filed against the account. With
 * no account the Worker refuses the capture -- correctly, since there would be
 * nobody to file it under -- and the message on the phone is about an account
 * rather than the Shortcut, which is confusing if this screen never mentioned
 * one.
 */
type AccountState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'unreachable'; message: string }
  | { kind: 'needs-registration' }
  | { kind: 'signed-out' }
  | { kind: 'signed-in' };

function AccountBlock({ baseUrl }: { baseUrl: string }): JSX.Element {
  const { toast } = useApp();
  const [state, setState] = useState<AccountState>({ kind: 'idle' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const check = useCallback(async (): Promise<void> => {
    if (!baseUrl.trim()) {
      setState({ kind: 'idle' });
      return;
    }
    setState({ kind: 'checking' });
    try {
      const status = await readDeploymentStatus(baseUrl);
      if (status.needsRegistration) {
        setState({ kind: 'needs-registration' });
        return;
      }
      setState({ kind: (await isSignedIn(baseUrl)) ? 'signed-in' : 'signed-out' });
    } catch (failure) {
      setState({
        kind: 'unreachable',
        message: failure instanceof Error ? failure.message : 'That address did not answer.',
      });
    }
  }, [baseUrl]);

  useEffect(() => {
    void check();
  }, [check]);

  async function submit(create: boolean): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      if (create) await registerAccount(baseUrl, email, password);
      else await signIn(baseUrl, email, password);
      setPassword('');
      toast(create ? 'Account created' : 'Signed in');
      await check();
    } catch (failure) {
      setError(
        failure instanceof AccountError
          ? failure.message
          : 'That did not work. Check the address and try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (state.kind === 'idle') return <></>;

  if (state.kind === 'checking') {
    return <p className="small muted">Checking the Worker...</p>;
  }

  if (state.kind === 'unreachable') {
    return (
      <div className="stack">
        <Notice tone="warning">{state.message}</Notice>
        <Button block onClick={() => void check()}>
          Try again
        </Button>
      </div>
    );
  }

  if (state.kind === 'signed-in') {
    return (
      <div className="settings-group">
        <div className="settings-row">
          <span className="settings-row__label">Account</span>
          <span className="settings-row__value">signed in</span>
        </div>
        <Button
          block
          onClick={async () => {
            await signOut(baseUrl);
            toast('Signed out');
            await check();
          }}
        >
          Sign out
        </Button>
      </div>
    );
  }

  const creating = state.kind === 'needs-registration';

  return (
    <div className="stack">
      <Notice tone={creating ? 'warning' : 'info'}>
        {creating
          ? 'This Worker has no account yet. Create one -- it is the only account it will ever accept, and a Shortcut capture cannot be saved until it exists.'
          : 'Sign in to this deployment to sync.'}
      </Notice>

      <Field label="Email" hint="Only an identifier. Nothing is ever sent to it.">
        {(props) => (
          <TextInput
            {...props}
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        )}
      </Field>

      <Field label="Password" hint="At least 10 characters. Not a bank password.">
        {(props) => (
          <TextInput
            {...props}
            type="password"
            autoComplete={creating ? 'new-password' : 'current-password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        )}
      </Field>

      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Button
        variant="accent"
        block
        disabled={busy || !email.trim() || password.length < 10}
        onClick={() => void submit(creating)}
      >
        {busy ? 'Working...' : creating ? 'Create the account' : 'Sign in'}
      </Button>
    </div>
  );
}

function SyncSheet({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const { settings, sync, updateSettings, refreshSync, toast } = useApp();
  const [baseUrl, setBaseUrl] = useState(settings.apiBaseUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function handleSync(retry = false): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      const result = retry ? await retryFailed() : await runSync();
      toast(`Sent ${result.pushed}, received ${result.pulled}`);
      await refreshSync();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Sync failed.');
      await refreshSync();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} title="Cloud sync" onClose={onClose}>
      <Notice tone="info">
        Sync is optional. With it off, everything stays on this device. With it on, your ledger is
        also kept on your own Cloudflare Worker -- which is also what lets an iPhone Shortcut record
        a payment while the app is closed.
      </Notice>

      <div className="settings-group" style={{ marginTop: 'var(--space-4)' }}>
        <div className="settings-row">
          <span className="settings-row__label">Sync with my Worker</span>
          <Switch
            checked={settings.syncEnabled}
            label="Cloud sync"
            onChange={(next) => void updateSettings({ syncEnabled: next })}
          />
        </div>
        <div className="settings-row">
          <span className="settings-row__label">Status</span>
          <span className="settings-row__value">
            {settings.syncEnabled ? sync.status : 'off'}
            {sync.pendingCount > 0 ? ` · ${sync.pendingCount} queued` : ''}
            {sync.failedCount > 0 ? ` · ${sync.failedCount} failed` : ''}
          </span>
        </div>
        <div className="settings-row">
          <span className="settings-row__label">Last synced</span>
          <span className="settings-row__value">
            {sync.lastSyncedAt ? sync.lastSyncedAt.slice(0, 16).replace('T', ' ') : 'never'}
          </span>
        </div>
      </div>

      <Field
        label="Worker address"
        hint="For example https://what-i-spent-api.your-name.workers.dev"
      >
        {(props) => (
          <TextInput
            {...props}
            type="url"
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            value={baseUrl}
            placeholder="https://..."
            onChange={(event) => setBaseUrl(event.target.value)}
            onBlur={() => void updateSettings({ apiBaseUrl: baseUrl.trim() })}
          />
        )}
      </Field>

      <div style={{ marginTop: 'var(--space-4)' }}>
        <AccountBlock baseUrl={settings.apiBaseUrl} />
      </div>

      {error ? <Notice tone="danger">{error}</Notice> : null}

      <div className="row">
        <Button
          variant="primary"
          className="grow"
          disabled={busy || !settings.syncEnabled}
          onClick={() => handleSync(false)}
        >
          {busy ? 'Syncing...' : 'Sync now'}
        </Button>
        <Button
          className="grow"
          disabled={busy || sync.failedCount === 0}
          onClick={() => handleSync(true)}
        >
          Retry failed
        </Button>
      </div>

      <p className="tiny faint" style={{ marginTop: 'var(--space-4)' }}>
        Nothing is ever dropped. If a change cannot reach the Worker it stays queued on this device
        and is retried, and you can always export a full backup from Settings.
      </p>
    </Sheet>
  );
}

/* ---------- delete ---------- */

function DeleteSheet({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const { transactions } = useApp();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleDelete(): Promise<void> {
    setBusy(true);
    try {
      await deleteAllLocalData();
      onDone();
      onClose();
      setTyped('');
      // A reload puts the app back in its first-run state with fresh defaults.
      window.location.assign('/');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} title="Delete all data" onClose={onClose}>
      <Notice tone="danger">
        This erases all {transactions.length} records, your categories, rules and settings from this
        device. It cannot be undone.
      </Notice>

      <p className="small muted" style={{ margin: 'var(--space-4) 0' }}>
        Export a backup first if you might want any of this back. If cloud sync is on, the copy on
        your Worker is not touched by this button -- use &ldquo;Erase cloud copy&rdquo; there as
        well.
      </p>

      <Field label="Type DELETE to confirm">
        {(props) => (
          <TextInput
            {...props}
            value={typed}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setTyped(event.target.value)}
          />
        )}
      </Field>

      <Button
        variant="danger"
        block
        disabled={typed !== 'DELETE' || busy}
        onClick={handleDelete}
      >
        {busy ? 'Deleting...' : 'Delete everything'}
      </Button>
    </Sheet>
  );
}
